import { ArrowRight, CalendarDays, MapPin, Phone, Star } from "lucide-react";
import { Link } from "react-router";
import { Faq, PhotoTile, ServiceMenu, StylistCard } from "@/components/blocks";
import { LogoLockup } from "@/components/Logo";
import { Card, Container, Eyebrow, LinkButton, PoleBar, SectionHeading, Spinner } from "@/components/ui";
import { site, values } from "@/content";
import { useSalon } from "@/hooks/useSalon";
import { hoursLabel, isOpenNow, WEEK } from "@/lib/format";

function Hero() {
  const { data } = useSalon();
  const open = data ? isOpenNow(data.salon.hours) : null;
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_80%_10%,#f6e9e6,transparent),radial-gradient(40%_40%_at_10%_90%,#f2ece6,transparent)]" />
      <Container className="grid items-center gap-12 py-16 md:grid-cols-[1.1fr_1fr] md:py-24">
        <div className="animate-fade-up">
          {open !== null && (
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-semibold shadow-soft ring-1 ring-line">
              <span className={open ? "size-2 rounded-full bg-emerald-500" : "size-2 rounded-full bg-steel"} aria-hidden />
              {open ? "Open now" : "Closed now"} · Downtown Anna
            </span>
          )}
          <h1 className="mt-6 font-display text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">
            Anna's hometown salon <span className="text-rust">since 1975.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted">
            Cuts, color, perms and classic sets for women, men and kids. Salon and barber under one roof, on the same corner of 4th Street for
            fifty years.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <LinkButton to="/book" size="lg">
              <CalendarDays className="size-5" aria-hidden /> Book online
            </LinkButton>
            <a href={`tel:${site.phone}`} className="inline-flex h-14 items-center gap-2 rounded-full px-6 font-semibold text-ink ring-1 ring-line hover:text-rust">
              <Phone className="size-5" aria-hidden /> {site.phoneDisplay}
            </a>
          </div>
          <p className="mt-6 flex items-center gap-2 text-sm text-muted">
            <MapPin className="size-4 text-rust" aria-hidden /> {site.address.line1}, {site.address.city}, {site.address.state}
          </p>
        </div>
        <div className="relative animate-fade-up [animation-delay:120ms]">
          <Card className="relative grid place-items-center px-6 py-14 sm:py-20">
            <LogoLockup />
            <div className="absolute top-6 right-6 rotate-6 rounded-full bg-rust px-4 py-2 text-center text-white shadow-lift">
              <span className="block font-display text-2xl leading-none">50</span>
              <span className="eyebrow text-[0.55rem]">years</span>
            </div>
          </Card>
        </div>
      </Container>
    </section>
  );
}

function Story() {
  return (
    <section className="py-20">
      <Container className="grid items-center gap-12 md:grid-cols-2">
        <div className="grid grid-cols-2 gap-4">
          <PhotoTile label="Salon photo" className="row-span-2 min-h-80" />
          <PhotoTile label="Betty at work" tone="sand" className="min-h-38" />
          <PhotoTile label="Classic set" tone="ink" className="min-h-38" />
        </div>
        <div>
          <SectionHeading
            eyebrow="Our story"
            title="Fifty years behind the chair."
            intro="Style & Curl opened its doors in 1975 and has been doing Anna's hair ever since. Owner Betty Noe has cut, colored and curled for generations of local families: grandmothers who came for their weekly set, and now their grandkids getting their first haircut."
          />
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {values.map((v) => (
              <div key={v.title}>
                <PoleBar className="w-10 rounded-full" />
                <h3 className="mt-4 font-display text-2xl">{v.title}</h3>
                <p className="mt-2 text-sm text-muted">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function ServicesPreview() {
  const { data, isLoading } = useSalon();
  return (
    <section className="bg-sand/60 py-20">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="Services" title="Something for everyone in the family." />
          <Link to="/services" className="inline-flex items-center gap-1 font-semibold text-rust">
            Full menu &amp; prices <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
        <div className="mt-10">{isLoading ? <Spinner label="Loading services" /> : data && <ServiceMenu services={data.services} limit={6} />}</div>
      </Container>
    </section>
  );
}

function TeamPreview() {
  const { data } = useSalon();
  return (
    <section className="py-20">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading eyebrow="The team" title="Friendly faces, steady hands." />
          <Link to="/team" className="inline-flex items-center gap-1 font-semibold text-rust">
            Meet everyone <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {data?.stylists.map((s) => <StylistCard key={s.id} stylist={s} />)}
        </div>
      </Container>
    </section>
  );
}

function ReviewsTeaser() {
  return (
    <section className="py-12">
      <Container>
        <Card className="flex flex-col items-start gap-6 bg-rust p-8 text-white ring-0 sm:flex-row sm:items-center sm:p-12">
          <div className="flex gap-1" aria-hidden>
            {Array.from({ length: 5 }, (_, i) => (
              <Star key={i} className="size-6 fill-white" />
            ))}
          </div>
          <div className="flex-1">
            <p className="font-display text-3xl">Loved by Anna for fifty years.</p>
            <p className="mt-2 text-white/80">See what neighbors say about their visits, or share your own.</p>
          </div>
          <Link to="/reviews" className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 font-semibold text-rust">
            Read reviews <ArrowRight className="size-4" aria-hidden />
          </Link>
        </Card>
      </Container>
    </section>
  );
}

function VisitStrip() {
  const { data } = useSalon();
  return (
    <section className="py-20">
      <Container className="grid gap-8 md:grid-cols-2">
        <div>
          <SectionHeading eyebrow="Visit us" title="Right on 4th Street." intro="In historic downtown Anna, a few steps from Main. Street parking out front." />
          <Card className="mt-8 p-6">
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
              {WEEK.map((d) => (
                <div key={d.key} className="contents">
                  <dt className="font-semibold">{d.label}</dt>
                  <dd className="text-muted">{data ? hoursLabel(data.salon.hours, d.key) : "…"}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
        <Card className="overflow-hidden">
          <iframe
            title="Map to Style & Curl"
            src={site.mapsEmbed}
            className="h-full min-h-80 w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Card>
      </Container>
    </section>
  );
}

export function Home() {
  return (
    <>
      <Hero />
      <Story />
      <ServicesPreview />
      <TeamPreview />
      <ReviewsTeaser />
      <section className="py-20">
        <Container>
          <SectionHeading eyebrow="Questions" title="Good to know." center />
          <div className="mt-12">
            <Faq />
          </div>
        </Container>
      </section>
      <VisitStrip />
      <Container>
        <Card className="flex flex-col items-center gap-4 px-6 py-14 text-center">
          <Eyebrow>Ready when you are</Eyebrow>
          <p className="max-w-xl font-display text-4xl">Pick a time that works for you. It takes under a minute.</p>
          <LinkButton to="/book" size="lg" className="mt-2">
            <CalendarDays className="size-5" aria-hidden /> Book your appointment
          </LinkButton>
        </Card>
      </Container>
    </>
  );
}
